export interface ITrackingHeaders {
  [key: string]: string | undefined;
  'X-Client-Latitude'?: string;
  'X-Client-Longitude'?: string;
  'X-Client-Geo-Accuracy'?: string;
}

interface IGeolocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/**
 * Solicita permissão de geolocalização do navegador.
 * Retorna as coordenadas se o usuário permitir, null caso contrário.
 */
export const requestGeolocation = (): Promise<IGeolocationData | null> => {
  return new Promise(resolve => {
    // Verifica se o navegador suporta geolocalização
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        // Usuário negou ou erro - retorna null
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 segundos de timeout
        maximumAge: 300000, // Cache de 5 minutos
      },
    );
  });
};

/**
 * Coleta dados de tracking do dispositivo para enviar via headers.
 * A geolocalização é coletada de forma assíncrona (solicita permissão ao usuário).
 *
 * @returns Headers com geolocalização (se disponível)
 */
export const getTrackingHeaders = async (): Promise<ITrackingHeaders> => {
  // Solicita geolocalização (não bloqueia se negado)
  const geoData = await requestGeolocation();

  const headers: ITrackingHeaders = {};

  // Adiciona coordenadas se disponíveis
  if (geoData) {
    headers['X-Client-Latitude'] = String(geoData.latitude);
    headers['X-Client-Longitude'] = String(geoData.longitude);
    headers['X-Client-Geo-Accuracy'] = String(geoData.accuracy);
  }

  return headers;
};

/**
 * Retorna headers de tracking vazios (versão síncrona).
 * Use quando não precisar de geolocalização.
 *
 * @returns Headers vazios (geolocalização requer async)
 */
export const getTrackingHeadersSync = (): ITrackingHeaders => {
  return {};
};
